import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useMatch, useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { validateAddEditForm, validateHeaderMappingForm } from './validations';
import Header from './components/Header';
import Buttons from './components/Buttons';
import TemplateDetailsTab from './components/TemplateDetailsTab';
import Tabs from './components/Tabs';
import HeaderMappingTab from './components/HeaderMappingTab';
import useFetch from '../../../hooks/useFetch';
import CommonService from '../../../services/common.service';
import { VALIDATION_FIELD_MAPPING, APP_SUCCESS_MESSAGES, GENERIC_LOOKUP_KEYS } from '../../../utils/appConstant';
import { AddEdit as AddEditInterface, AddEditApi } from '../../../interfaces/template.interface';
import { Validation, HeadCellType, TableContextProvider } from '../../../interfaces/common';
import { buildHeadCells } from '../../../utils/methods';
import HeaderMappingRow from './components/HeaderMappingRow';
import TemplateService from '../../../services/template.service';
import { Modules, AuthRoutes, RouteParamsTypes } from '../../../enums';
import Spinner from '../../../components/Spinner';
import Toast from '../../../components/Toast';
import { STATUS_CODE } from '../../../utils/apiConstant';

const initialFormData: AddEditInterface = {
  name: '',
  code: '',
  description: '',
  status: 'true', // Default active
  fileType: '',
};

const initialDropdownSearchData = {
  fileType: '',
};

const initialValidation: Validation = { validate: true, fields: [] };

const AddEditView: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const { id } = useParams();
  const view = !!useMatch(Modules.templates + AuthRoutes.templates + RouteParamsTypes.view + '/:id');
  const edit = !!useMatch(Modules.templates + AuthRoutes.templates + RouteParamsTypes.edit + '/:id');
  const navigate = useNavigate();

  const [formData, setFormData] = useState<AddEditInterface>(initialFormData);
  const [dropdownSearchData, setDropdownSearchData] = useState<any>(initialDropdownSearchData);
  const [loading, setLoading] = useState(false);
  const [actionTwoLoading, setActionTwoLoading] = useState<boolean>(false);
  const [validation, setValidation] = useState<Validation>(initialValidation);
  const [tab, setTab] = useState<'templateDetails' | 'headerMapping'>('templateDetails');
  const [headerMappings, setHeaderMappings] = useState<any[]>([]);
  const [headerMappingValidation, setHeaderMappingValidation] = useState<Validation>(initialValidation);
  const [headerOptions, setHeaderOptions] = useState<any[]>([]);
  const [savedMappings, setSavedMappings] = useState<any[]>([]);

  useEffect(() => {
    setFormData(initialFormData);
    setDropdownSearchData(initialDropdownSearchData);
    setValidation(initialValidation);
    setTab(location?.state?.tab ?? 'templateDetails');
    setHeaderMappings([]);
    setHeaderMappingValidation(initialValidation);
    setHeaderOptions([]);
    setSavedMappings([]);
  }, [location.key]);

  // ... hooks ...

  // Fetch Template Header Lookup
  useFetch({
    service: TemplateService,
    func: 'getTemplateHeaderLookup',
    payload: null,
    dependency: [],
    onSuccess: (response: any) => {
      if (response && Array.isArray(response)) {
        const options = response.map((item: any) => ({
          label: item.name,
          value: item.id  // Using ID as value for saving
        }));
        setHeaderOptions(options);
      }
    }
  });

  // Fetch Existing Mappings
  const { loading: mappingLoading } = useFetch({
    service: TemplateService,
    func: 'getTemplateHeaders',
    payload: id,
    dependency: [id],
    skip: !id,
    onSuccess: (response: any) => {
      if (response && Array.isArray(response)) {
        setSavedMappings(response);
      }
    }
  });

  // Merge headers and saved mappings
  useEffect(() => {
    if (headerOptions.length > 0) {
      const newMappings = headerOptions.map((opt, index) => {
        const found = savedMappings.find((m: any) => m.templateheader_id === opt.value);
        return {
          id: index + 1,
          header: opt.value, // Auto-select the header
          mapping: found ? found.mapping_key : '', // Pre-fill mapping if exists
          isChecked: !!found, // Check row if mapping exists
        };
      });
      setHeaderMappings(newMappings);
    }
  }, [headerOptions, savedMappings, setHeaderMappings]);

  const headCells: HeadCellType[] = useMemo(() => buildHeadCells(
    [
      VALIDATION_FIELD_MAPPING.actions,
      VALIDATION_FIELD_MAPPING.templateHeader,
      VALIDATION_FIELD_MAPPING.textboxMapping,
    ],
    'templateHeaderMappingList'
  ), []);

  const handleCheckChange = useCallback((mappingId: number, isChecked: boolean) => {
    setHeaderMappings((prevMappings) => {
      const updatedMappings = prevMappings.map((item) => (item.id === mappingId ? { ...item, isChecked, mapping: isChecked ? item.mapping : '' } : item));

      if (!isChecked) {
        setHeaderMappingValidation((prevValidation) => {
          const filteredFields = prevValidation.fields.filter((field) => !field.field.endsWith(`-${mappingId}`));
          return {
            validate: filteredFields.length === 0,
            fields: filteredFields
          };
        });
      }

      return updatedMappings;
    });
  }, [setHeaderMappings, setHeaderMappingValidation]);

  const handleMappingInputChange = useCallback((mappingId: number, field: string, value: string) => {
    setHeaderMappings((prevMappings) => {
      const updatedMappings = prevMappings.map((item) => (item.id === mappingId ? { ...item, [field]: value } : item));

      setHeaderMappingValidation((prev) => {
        const newValidation = validateHeaderMappingForm(updatedMappings, mappingId);

        const filteredFields = prev.fields.filter((f) => !f.field.endsWith(`-${mappingId}`));
        const fields = [...filteredFields, ...newValidation.fields];

        return {
          validate: fields.length === 0,
          fields: fields
        }
      });

      return updatedMappings;
    });
  }, [setHeaderMappings, setHeaderMappingValidation]);

  const handleMappingInputBlur = useCallback((mappingId: number, field: string, value: string) => {
    setHeaderMappings((prevMappings) => {
      const updatedMappings = prevMappings.map((item) => (item.id === mappingId ? { ...item, [field]: value } : item));

      setHeaderMappingValidation((prev) => {
        const newValidation = validateHeaderMappingForm(updatedMappings, mappingId);

        const filteredFields = prev.fields.filter((f) => !f.field.endsWith(`-${mappingId}`));
        const fields = [...filteredFields, ...newValidation.fields];

        return {
          validate: fields.length === 0,
          fields: fields
        }
      });

      return updatedMappings;
    });
  }, [setHeaderMappingValidation, setHeaderMappings]);

  const headerMappingTableValues: TableContextProvider = useMemo(() => {
    return {
      row: HeaderMappingRow,
      data: headerMappings,
      pagination: false,
      headCells,
      order: 'asc' as any,
      orderBy: '',
      page: 1,
      perPage: 10,
      total: headerMappings.length,
      loading: mappingLoading,
      noDataFoundMessage: t('common.table.noRecordsFound'),
      setPage: () => { },
      setPerPage: () => { },
      setOrder: () => { },
      setOrderBy: () => { },
      rowProps: {
        onInputChange: handleMappingInputChange,
        onInputBlur: handleMappingInputBlur,
        onCheckChange: handleCheckChange,
        headerOptions,
        validation: headerMappingValidation,
      },
    };
  }, [headCells, headerMappings, t, headerOptions, mappingLoading, handleMappingInputChange, handleMappingInputBlur, handleCheckChange, headerMappingValidation]);

  const handleTabChange = useCallback((e: any, value: 'templateDetails' | 'headerMapping') => {
    setTab(value);
  }, []);

  const { data: allFileTypes } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.fileType, searchText: '' },
    dependency: [],
  });

  const { data: fileTypes } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.fileType, searchText: dropdownSearchData.fileType },
    dependency: [dropdownSearchData.fileType],
    skip: !dropdownSearchData.fileType,
  });

  const { loading: getContentLoading } = useFetch({
    service: TemplateService,
    func: 'get',
    payload: id,
    dependency: [id, location.key],
    skip: !id,
    onSuccess: async (response: any) => {
      setFormData({
        id: id,
        name: response.name,
        code: response.code || '',
        description: response.description,
        status: String(response.is_active),
        fileType: response.extension?.code,
      });
    },
    onError: (error: any) => {
      // if (error.code === STATUS_CODE.notFound) navigate(Modules.templates + AuthRoutes.templates);
      // For now, allow view since we are mocking
    },
  });

  const handleInputChange = useCallback(
    (event: any, name: string) => {
      let value: any = event.target.value;

      setFormData((prev) => {
        return {
          ...prev,
          [name]: value,
        };
      });
    },
    [setFormData]
  );

  const handleDropdownSearchInputChange = useCallback(
    (event: any, name: string) => {
      setDropdownSearchData((prev: any) => {
        return {
          ...prev,
          [name]: event.target.value,
        };
      });
    },
    [setDropdownSearchData]
  );

  const handleInputBlur = useCallback(
    (event: any, name: string) => {
      let value = event.target.value.trim();

      let newValidation = validateAddEditForm(
        {
          ...formData,
          [name]: value,
        },
        name
      );
      setValidation(newValidation);
    },
    [formData]
  );

  const handleSubmit = useCallback(
    async (action: '1' | '2', event?: React.FormEvent) => {
      event && event.preventDefault();

      if (tab === 'headerMapping') {
        const newValidation = validateHeaderMappingForm(headerMappings);
        setHeaderMappingValidation(newValidation);
        if (!newValidation.validate) return;

        if (action === '1') setLoading(true);
        try {
          const validMappings = headerMappings
            .filter((m) => m.isChecked && m.header && m.mapping?.trim())
            .map((m) => ({
              templateheader_id: m.header,
              mapping_key: m.mapping.trim()
            }));

          const response: any = await TemplateService.saveTemplateHeaders(id as string, { mappings: validMappings });

          if (response.success !== false) {
            toast.success(<Toast
              message={APP_SUCCESS_MESSAGES.headerMappingUpdated}
            />, { icon: false });
          }
          setLoading(false);
        } catch (err) {
          setLoading(false);
        }
        return;
      }

      const newValidation = validateAddEditForm(formData);
      setValidation(newValidation);
      if (!newValidation.validate) return;

      if (action === '1') setLoading(true);
      else setActionTwoLoading(true);

      try {
        const payload: AddEditApi = {
          name: formData.name.trim(),
          code: formData.code.trim(),
          description: formData.description.trim(),
          is_active: formData.status === 'true',
          extension: formData.fileType,
        };

        let response: any;
        if (!edit) {
          response = await TemplateService.add(payload);
        } else if (id) {
          response = await TemplateService.edit(id, payload);
        }

        if (response.success !== false) {
          toast.success(<Toast
            message={edit ? t('templates.addEdit.pageTexts.form.editSuccess') : t('templates.addEdit.pageTexts.form.addSuccess')}
          />, { icon: false });

          if (edit) {
            navigate(Modules.templates + AuthRoutes.templates);
          } else if (action === '2') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            const input = document.getElementById(VALIDATION_FIELD_MAPPING.name);
            input && input.focus();
            setFormData(initialFormData);
            setValidation(initialValidation);
          } else {
            navigate(`${Modules.templates}${AuthRoutes.templates}`, { replace: true });
          }
        }

        if (action === '1') setLoading(false);
        else setActionTwoLoading(false);
      } catch (err: any) {
        if (action === '1') setLoading(false);
        else setActionTwoLoading(false);
      }
    },
    [id, edit, formData, navigate, t, tab, headerMappings, setHeaderMappingValidation]
  );

  const handleKeyDown = useCallback(
    (e: any, name?: string) => {
      if (e.key === 'Enter') handleSubmit('1');
    },
    [handleSubmit]
  );

  const handleCancel = () => {
    navigate(Modules.templates + AuthRoutes.templates);
  };

  let formContent;

  if (getContentLoading) {
    formContent = (
      <div className="card-header text-center">
        <Spinner />
      </div>
    );
  } else if (tab === 'templateDetails') {
    formContent = (
      <TemplateDetailsTab
        edit={edit}
        view={view}
        formData={formData}
        fileTypes={dropdownSearchData.fileType ? fileTypes : allFileTypes}
        allFileTypes={allFileTypes}
        dropdownSearchData={dropdownSearchData}
        onDropdownSearchChange={handleDropdownSearchInputChange}
        validation={validation}
        onKeyDown={handleKeyDown}
        onInputBlur={handleInputBlur}
        onInputChange={handleInputChange}
      />
    );
  } else if (tab === 'headerMapping') {
    formContent = (
      <HeaderMappingTab tableValues={headerMappingTableValues} />
    );
  }

  return (
    <>
      <Header id={id} view={view} edit={edit} />

      <div className="card mb-6">
        <h5 className="card-header">
          {!id
            ? t('templates.addEdit.pageTexts.title.addTitle')
            : view
              ? t('templates.addEdit.pageTexts.title.viewTitle')
              : t('templates.addEdit.pageTexts.title.editTitle')}
        </h5>
        <hr className="m-0"></hr>
        <Tabs tab={tab} add={!edit && !view} onTabChange={handleTabChange} />

        <hr className="m-0"></hr>

        {formContent}
        <hr className="m-0"></hr>
        <Buttons
          tab={tab}
          actionTwoLoading={actionTwoLoading}
          loading={loading}
          view={view}
          edit={edit}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      </div>
    </>
  );
};

export default AddEditView;
